import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'

import { LoginPage } from '@/pages/Login'
import { RegisterPage } from '@/pages/Register'
import { DashboardPage } from '@/pages/Dashboard'
import { RecipeDiscovery } from '@/pages/RecipeDiscovery'
import { RecipeDetailPage } from '@/pages/RecipeDetailPage'
import { MealPlannerPage } from '@/pages/MealPlannerPage'
import { ShoppingListPage } from '@/pages/ShoppingListPage'
import { FavoritesPage } from '@/pages/FavoritesPage'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AppLayout } from '@/components/layout/AppLayout'
import { useAuth } from '@/hooks/useAuth'
import { LandingPage } from '@/pages/LandingPage'
import { OAuthCallbackPage } from '@/pages/OAuthCallbackPage'

// Create a client with optimized settings to avoid rate limiting
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false, // Disable automatic refetch on reconnect
      staleTime: 10 * 60 * 1000, // 10 minutes (increased from 5)
      gcTime: 15 * 60 * 1000, // 15 minutes (increased garbage collection time)
      refetchInterval: false, // Disable automatic periodic refetch
    },
    mutations: {
      retry: 1,
    },
  },
})

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth()
  
  // Initialize favorites synchronization for authenticated users
  // Temporarily disabled to avoid rate limiting during development
  // TODO: Re-enable after optimizing rate limits
  /*
  if (isAuthenticated) {
    // This hook will handle automatic sync and online/offline detection
    useHybridFavorites();
  }
  */

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-primary"></div>
      </div>
    )
  }

  return (
    <Routes>
      {/* OAuth callback — must be before /login so tokens are stored before auth check */}
      <Route path="/auth/callback" element={<OAuthCallbackPage />} />
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
        } 
      />
      <Route 
        path="/register" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
        } 
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout><DashboardPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <ProtectedRoute>
            <AppLayout><RecipeDiscovery /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/:id"
        element={
          <ProtectedRoute>
            <AppLayout><RecipeDetailPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/meal-planner"
        element={
          <ProtectedRoute>
            <AppLayout><MealPlannerPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shopping-list"
        element={
          <ProtectedRoute>
            <AppLayout><ShoppingListPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shopping-list/:mealPlanId"
        element={
          <ProtectedRoute>
            <AppLayout><ShoppingListPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <AppLayout><FavoritesPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<LandingPage />} />
      <Route
        path="*"
        element={
          <div className="flex min-h-screen items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">404</h1>
              <p className="text-gray-600 mb-4">Página no encontrada</p>
              <a
                href={isAuthenticated ? "/dashboard" : "/login"}
                className="text-primary underline-offset-4 hover:underline"
              >
                Volver al inicio
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppRoutes />
      </Router>
      <Toaster
        position="top-center"
        richColors
        toastOptions={{ className: 'text-sm' }}
      />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
